<script setup lang="ts">
const props = defineProps<{
  disabled?: boolean,
  label?: string
}>()
const value = defineModel<boolean>()

const toggleValue = () => {
  if (props.disabled) return
  value.value = !value.value
}
</script>

<template>
  <!-- role/tabindex/aria-checked rather than a bare div: without them the control is
       invisible to screen readers and unreachable by keyboard, which matters most on the
       settings page where the toggle IS the control. Space and Enter both activate,
       matching native switch behaviour. -->
  <div
    class="wad4-switch"
    :class="{ 'wad4-switch--disabled': disabled }"
    role="switch"
    :tabindex="disabled ? -1 : 0"
    :aria-checked="!!value"
    :aria-disabled="!!disabled"
    :aria-label="label"
    @click="toggleValue"
    @keydown.space.prevent="toggleValue"
    @keydown.enter.prevent="toggleValue"
  >
    <div class="wad4-switch__bar" :class="{ 'wad4-switch__bar--active': value }"></div>
    <div class="wad4-switch__thumb" :class="{ 'wad4-switch__thumb--active': value }"></div>
  </div>
</template>

<style lang="scss">
.wad4-switch {
  width: 40px;
  height: 20px;
  position: relative;
  cursor: pointer;
  flex-shrink: 0;
  flex-grow: 0;

  &--disabled {
    opacity: 0.5;
    pointer-events: none;
  }

  // Tabbable now, so it needs a visible focus state; the default outline would hug the
  // 40x20 box and clip against the thumb.
  &:focus-visible {
    outline: 2px solid $wad4-c-background-bg-brand-solid;
    outline-offset: 3px;
    border-radius: 7px;
  }

  &__bar {
    position: absolute;
    left: 0;
    top: 15%;
    width: 100%;
    height: 70%;
    background-color: $wad4-c-foreground-fg-tertiary;
    opacity: 0.5;
    border-radius: 7px;

    &--active {
      background-color: $wad4-c-background-bg-brand-solid;
    }
  }

  &__thumb {
    position: absolute;
    left: 0;
    top: 0;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background-color: $wad4-c-background-bg-primary;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
    transition: .4s cubic-bezier(.25,.8,.25,1);

    &--active {
      transform: translate3d(20px, 0, 0);
      background-color: $wad4-c-background-bg-brand-solid;
    }
  }

}
</style>
